import React from 'react';

import { Grid, Box, Typography, FormControlLabel, Switch, Divider } from '@mui/material';
import Container from 'components/Container';
import ContainerBox from 'components/ContainerBox';
import { EmailNotification, NotificationRecipient } from 'common/types';
import { useForm, Controller } from 'react-hook-form';
import { Prompt } from 'react-router';
import { api } from 'common/frontend/api';

import { useDispatch, useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { useTranslation } from 'services/localization';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SaveButton from 'common/components/SaveButton';

import { Routes } from 'routing';
import RecipientSelection from './RecipientSelection';

const defaultEmailSetting: EmailNotification = {
	enabled: false,
	emails: null,
};

type FormValues = {
	onlineReservations: EmailNotification;
	extensions: EmailNotification;
	recipient: NotificationRecipient;
};

const Notifications = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const shopInfo = useSelector(ShopSelectors.activeShopInfo);
	const publicInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const shopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	// We only want to display the email in case the account has only 1 store
	const singleStoreEmail = shopLocations.length === 1 ? shopLocations[0].email : undefined;

	const form = useForm<FormValues>({
		defaultValues: {
			onlineReservations: shopInfo?.emailNotifications?.onlineReservations ?? defaultEmailSetting,
			extensions: shopInfo?.emailNotifications?.extensions ?? defaultEmailSetting,
			recipient: shopInfo?.emailNotifications?.recipient ?? 'store',
		},
	});
	const { isDirty, isSubmitting } = form.formState;

	const onSubmit = async (data: FormValues) => {
		try {
			await api().shops.doc(shopInfo!.id).update({
				'emailNotifications.onlineReservations': data.onlineReservations,
				'emailNotifications.extensions': data.extensions,
				'emailNotifications.recipient': data.recipient,
			});
			form.reset(data);
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
	};

	return (
		<>
			<SetPageTitle
				priority={2}
				title={t('account.notifications.pageTitle', 'Notifications')}
				rightContent={
					<SaveButton
						disabled={!isDirty}
						loading={isSubmitting}
						onClick={form.handleSubmit(onSubmit)}
						t={t}
					/>
				}
			/>
			<SetPageNavAction
				priority={2}
				backAction={{ to: Routes.ACCOUNT, title: t('account.pageTitle', 'Account') }}
				quickLinks={[]}
			/>
			<Prompt when={isDirty} message={() => t('common:prompts.unsavedChanges')} />
			<Container>
				<ContainerBox>
					<Grid container spacing={4}>
						<Grid item xs={12} md={6}>
							<Typography variant="h6">
								{t('account.notifications.emailNotifications', 'Email notifications')}
							</Typography>
							<Box pr={4}>
								<Typography variant="body2" color="secondary">
									{t(
										'account.notifications.emailNotificationsInfo',
										'Choose how you want to be notified and manage recipients',
									)}
								</Typography>
							</Box>
						</Grid>
						<Grid item xs={12} md={6}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Controller
										name="onlineReservations"
										control={form.control}
										render={(renderProps) => (
											<>
												<FormControlLabel
													control={
														<Switch
															checked={renderProps.field.value.enabled}
															onChange={(e) =>
																renderProps.field.onChange({
																	...renderProps.field.value,
																	enabled: e.target.checked,
																})
															}
															color="primary"
														/>
													}
													label={t(
														'account.notifications.newOnlineReservations2',
														'New online orders',
													)}
												/>
											</>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									<Controller
										name="extensions"
										control={form.control}
										render={(renderProps) => (
											<>
												<FormControlLabel
													control={
														<Switch
															checked={renderProps.field.value.enabled}
															onChange={(e) =>
																renderProps.field.onChange({
																	...renderProps.field.value,
																	enabled: e.target.checked,
																})
															}
															color="primary"
														/>
													}
													label={t('account.notifications.rentalExtensions', 'Rental extensions')}
												/>
											</>
										)}
									/>
								</Grid>
							</Grid>
							<Box my={4}>
								<Divider />
							</Box>
							<Controller
								name="recipient"
								control={form.control}
								render={(renderProps) => (
									<RecipientSelection
										merchantEmail={publicInfo?.email}
										storeEmail={singleStoreEmail}
										value={renderProps.field.value}
										onChange={(recipient) => renderProps.field.onChange(recipient)}
									/>
								)}
							/>
						</Grid>
					</Grid>
				</ContainerBox>
			</Container>
		</>
	);
};

export default Notifications;
